/**
 * Build the canonical RGF filename for an asset uploaded from the local
 * filesystem. Matches the server-side pattern used for Impact assets so
 * Brandfolder and NocoDB stay consistent across sources.
 *
 * Spec: docs/asset-management-upload-photo-sources.md §Filename Conventions
 *
 *   RGF-{PROGRAM}-{VILLAGE_NAME}-{VILLAGE_ID}-{DISTRICT_NAME}-{DATE_TIME}.{ext}
 *
 *   - PROGRAM:       SD → "SC"; everything else passes through unchanged.
 *   - VILLAGE_NAME:  spaces → '-', '(' and ')' stripped.
 *   - VILLAGE_ID:    raw NocoDB / location lookup id (e.g. `V_37059`).
 *   - DISTRICT_NAME: same sanitisation as village name.
 *   - DATE_TIME:     `MMDDYYYY-HHMMSS`, or "unknown" when no date is
 *                    available (matches the Impact fallback).
 *   - ext:           lowercase, matching the file's MIME type.
 *
 * Every segment is left as-is when missing (we collapse empty pieces so
 * we never emit `RGF--V_37059-`), but `unknown` is preferred so the
 * file still looks structurally consistent with Impact assets.
 */

const SD_PROGRAM_FOR_FILENAME = 'SC';

/** Sanitise village / district names: spaces → '-', strip parentheses. */
function sanitiseSegment(value: string | null | undefined): string {
  if (!value) return '';
  return value.replace(/\s+/g, '-').replace(/[()]/g, '');
}

function programForFilename(program: string | null | undefined): string {
  const raw = (program ?? '').trim();
  if (!raw) return '';
  return raw.toUpperCase() === 'SD' ? SD_PROGRAM_FOR_FILENAME : raw;
}

/** Pad a non-negative integer to `width` characters with leading zeros. */
function pad(n: number, width: number): string {
  const s = String(n);
  return s.length >= width ? s : '0'.repeat(width - s.length) + s;
}

/**
 * Format `MMDDYYYY-HHMMSS` from a Date. Uses local time (matches what
 * the original photo timestamp typically represents — capture wall-clock
 * time on the device).
 */
function formatDateSerial(date: Date): string {
  const mm = pad(date.getMonth() + 1, 2);
  const dd = pad(date.getDate(), 2);
  const yyyy = pad(date.getFullYear(), 4);
  const hh = pad(date.getHours(), 2);
  const mi = pad(date.getMinutes(), 2);
  const ss = pad(date.getSeconds(), 2);
  return `${mm}${dd}${yyyy}-${hh}${mi}${ss}`;
}

const MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
};

/**
 * Pick a lowercase extension for the file: trust the MIME map when
 * available, otherwise fall back to the original filename's extension,
 * defaulting to `jpg`.
 */
export function pickExtension(file: File): string {
  const fromMime = MIME_TO_EXT[file.type?.toLowerCase() ?? ''];
  if (fromMime) return fromMime;
  const dotIdx = file.name.lastIndexOf('.');
  if (dotIdx > 0 && dotIdx < file.name.length - 1) {
    return file.name.slice(dotIdx + 1).toLowerCase();
  }
  return 'jpg';
}

export interface RgfFilenameParts {
  program?: string | null;
  villageName?: string | null;
  villageId?: string | null;
  districtName?: string | null;
  /** Optional capture date; falls back to "unknown" when absent. */
  date?: Date | null;
  /** Lowercase extension without the leading dot (e.g. `jpg`). */
  ext: string;
}

export function buildRgfFilename(parts: RgfFilenameParts): string {
  const program = programForFilename(parts.program);
  const village = sanitiseSegment(parts.villageName);
  const district = sanitiseSegment(parts.districtName);
  const villageId = (parts.villageId ?? '').trim();
  const dateSerial = parts.date ? formatDateSerial(parts.date) : 'unknown';
  const segments = ['RGF', program, village, villageId, district, dateSerial].filter(
    (s) => s && s.length > 0,
  );
  const base = segments.join('-');
  const ext = (parts.ext || 'jpg').toLowerCase().replace(/^\./, '');
  return `${base}.${ext}`;
}
